/**
 * @file useSupabaseStore.js
 * @location src/integrations/supabase/useSupabaseStore.js
 * @role 로컬 스토어(useAppStore) 위에 Supabase 인증·하이드레이션·동기화를 얹는 브리지.
 *
 *  - 세션 변화 → fetchWorkspace → HYDRATE + Realtime 구독 (phase: loading/signin/join/ready)
 *  - act 래핑: sync.js 의 ACTION_HANDLERS 한 곳에 mode/rpc/reduce/rollback 선언. 분기는 mode 만 본다.
 *  - ADD_TASK/ADD_TEMPLATE 는 클라이언트가 uuid 발급 → 낙관적 row 와 서버 row id 일치
 *  - wrappedAct 는 Promise 반환 — 호출 측이 await/catch 가능 (모달 close 타이밍, 토스트 등)
 *
 *  ⚠️ 온보딩(팀 생성·초대 수락)은 auth.onCreateTeam/onAcceptInvite 로 처리 — 끝나면 refetchWorkspace 직접 호출.
 *
 *  ── source-of-truth 규약 ──
 *  온라인: Supabase = 진실원천. localStorage(useAppStore saveToStorage)는 캐시 — 리로드 시 outbox 큐 복원 +
 *  오프라인 표시용. (재)연결마다 fetchWorkspace→HYDRATE 가 로컬 tasks/members/templates 를 서버본으로 덮어쓴다.
 *  즉 로컬 영속은 권위가 아니라 부팅 시드/큐 보관소 — 충돌 시 항상 서버(HYDRATE) 승. (이중저장은 의도된 설계, 제거 X)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth.js';
import { fetchWorkspace } from './api/workspace.js';
import { createTeam, acceptInvite } from './api/members.js';
import { subscribeWorkspace } from './realtime.js';
import { ACTION_HANDLERS } from './sync.js';
import { sendPushNotification } from './api/push.js';

function genId() {
  return (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`);
}

function emitSyncError(type, e) {
  console.error('[supabase] sync failed:', type, e);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('supabase:error', { detail: { type, error: e } }));
  }
}

function makeOutboxEntry(type, payload) {
  return { id: genId(), type, payload };
}

export function useSupabaseStore(store) {
  const { dispatch, act } = store;
  const { session, user, loading, passwordRecovery, signInWithPassword, signUpWithPassword, signInWithGoogle, sendPasswordReset, updatePassword, exitPasswordRecovery, signOut } = useAuth();
  const [phase, setPhase] = useState('loading');
  const [deletedScope, setDeletedScope] = useState(/** @type {'team'|'member'|null} */ (null));

  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const outboxRef = useRef(store.S.outbox);
  useEffect(() => { outboxRef.current = store.S.outbox; }, [store.S.outbox]);

  // Read-only 가드용 — subscription.status 가 past_due/cancelled 면 mutation 차단
  const subStatusRef = useRef(store.S.subscription?.status);
  useEffect(() => { subStatusRef.current = store.S.subscription?.status; }, [store.S.subscription?.status]);

  const flushingRef = useRef(false);

  const refetchWorkspace = useCallback(async () => {
    const u = userRef.current;
    if (!u) return;
    try {
      const ws = await fetchWorkspace(u.id);
      if (!ws) { setPhase('join'); return; }
      dispatch({ type: 'HYDRATE', payload: ws });
      setDeletedScope(ws.deletedScope ?? null);
      setPhase('ready');
    } catch (e) {
      console.error('[supabase] hydrate failed:', e);
      setPhase('join');
    }
  }, [dispatch]);

  const flushOutbox = useCallback(async () => {
    if (flushingRef.current) return;        // 동시 실행 가드
    flushingRef.current = true;
    try {
      const queue = (outboxRef.current ?? []).slice();   // ref-based — stable identity
      for (const entry of queue) {
        const handler = ACTION_HANDLERS[entry.type];
        if (!handler) {
          dispatch({ type: 'OUTBOX_REMOVE', id: entry.id });
          continue;
        }
        try {
          const result = await handler.rpc(entry.payload);
          if (handler.mode === 'authoritative' && handler.reduce) {
            const a = handler.reduce(result, entry.payload);
            if (a) dispatch(a);
          }
          dispatch({ type: 'OUTBOX_REMOVE', id: entry.id });
        } catch (e) {
          emitSyncError(entry.type, e);
          // 큐에 남김 — 다음 트리거 때 재시도
        }
      }
    } finally {
      flushingRef.current = false;
    }
  }, [dispatch]);

  useEffect(() => {
    if (loading) { setPhase('loading'); return; }
    if (!session) { setPhase('signin'); return; }
    let cancelled = false;
    let unsub;
    (async () => {
      await refetchWorkspace();
      if (cancelled) return;
      unsub = subscribeWorkspace(dispatch, {
        onReconnect: () => { refetchWorkspace(); flushOutbox(); },
        onDisconnect: () => emitSyncError('REALTIME_DISCONNECT', new Error('realtime disconnected')),
      });
    })();
    return () => { cancelled = true; if (unsub) unsub(); };
  }, [session, loading, dispatch, refetchWorkspace, flushOutbox]);

  const wrappedAct = useCallback((type, patch = {}) => {
    let p = patch;
    if (type === 'ADD_TASK')          p = { ...patch, task: { id: genId(), ...patch.task } };
    else if (type === 'ADD_TEMPLATE') p = { ...patch, template: { id: genId(), ...patch.template } };

    const handler = ACTION_HANDLERS[type];
    if (!handler) {
      act(type, p);  // 로컬 전용 (nav/ui 등) — read-only 모드에서도 통과
      return Promise.resolve();
    }

    // Read-only 가드 — past_due/cancelled 시 mutation RPC 차단
    // (로컬 전용 액션은 위에서 이미 return — 여기 도달 시점은 RPC-mapped 만)
    const sub = subStatusRef.current;
    if (sub === 'past_due' || sub === 'cancelled') {
      return Promise.reject(new Error('읽기 전용 모드: 결제 정보를 갱신해 주세요'));
    }

    if (handler.mode === 'optimistic') {
      act(type, p);  // 낙관적 로컬 반영
      return handler.rpc(p).then((result) => {
        // ADD_TASK 성공 → 담당자에게 push (fire-and-forget)
        if (type === 'ADD_TASK' && p.task?.aee) {
          sendPushNotification({
            memberId: p.task.aee,
            taskId: p.task.id,
            title: p.task.title,
            body: p.task.note ?? '',
            url: '/',
          });
        }
        return result;
      }).catch((e) => {
        emitSyncError(type, e);
        dispatch({ type: 'OUTBOX_ENQUEUE', entry: makeOutboxEntry(type, p) });
        const inverse = handler.rollback?.(p);
        if (inverse) dispatch(inverse);       // 정밀 역동작 (ADD 류)
        else refetchWorkspace();              // EDIT/TOGGLE/DELETE 등: 전체 재동기화
        throw e;
      });
    }

    // authoritative — RPC 응답으로만 로컬 반영 (서버 발행 invite_code 등)
    return handler.rpc(p).then((result) => {
      const action = handler.reduce?.(result, p);
      if (action) dispatch(action);
      return result;
    }).catch((e) => {
      emitSyncError(type, e);
      throw e;
    });
  }, [act, dispatch, refetchWorkspace]);

  useEffect(() => {
    const onOnline = () => { flushOutbox(); };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [flushOutbox]);

  // 부팅 시 1회 stale drop
  useEffect(() => {
    dispatch({ type: 'OUTBOX_DROP_STALE' });
  }, [dispatch]);

  const onCreateTeam = useCallback(async (name) => {
    await createTeam('', name);
    await refetchWorkspace();
    // 신규 팀(매니저)은 멤버 탭으로 착지 — 멤버 초대가 첫 할 일 (razor ㉒ 단일 매니저, SETUP_WORKSPACE 와 동일 의도).
    // HYDRATE 는 activeNav 를 건드리지 않아 초기값 'tasks' 로 남으므로 명시 전환. (초대 수락 onAcceptInvite 는 업무 유지)
    dispatch({ type: 'SET_NAV', nav: 'members' });
  }, [refetchWorkspace, dispatch]);

  const onAcceptInvite = useCallback(async ({ code, name }) => {
    await acceptInvite({ code, name, email: userRef.current?.email });
    await refetchWorkspace();
  }, [refetchWorkspace]);

  return {
    ...store,
    act: wrappedAct,
    flushOutbox,
    refetchWorkspace,
    deletedScope,
    auth: { phase, email: user?.email, passwordRecovery, signInWithPassword, signUpWithPassword, signInWithGoogle, sendPasswordReset, updatePassword, exitPasswordRecovery, signOut, onCreateTeam, onAcceptInvite },
  };
}
